import {noop} from '@benchling/js-utils';
import React from 'react';
import _ from 'underscore';

import {SelectTreeProps} from 'ui-library/select-tree/SelectTree';
import {ElementMouseEvent} from 'util/Types';

import {TreeItem, TreeItemStateMap} from './SelectTreeConstants';
import SelectTreeListItem from './SelectTreeListItem';

export interface SelectTreeNodeProps {
  disabled?: boolean;
  childrenDisabled?: boolean;
  item: TreeItem;
  onClickItem: (id: string) => void;
  loadMoreFromIds: ((ids: Array<string>) => void) | null;
  itemStateMap: TreeItemStateMap;
  itemTypeName: string;
  toggleExpanded: (id: string) => void;
  selectedIds: Array<string>;
  onChangeSelectedIds: (selectedIds: Array<string>) => void;
  isParentSelected: boolean;
  multiple: boolean;
  getNextSelectedIds: (id: string, selectedIds: Array<string>) => Array<string>;
  isLoadedItemExpandable?: ((item: TreeItem) => boolean) | null;
  requiredRootIds?: Array<string>;
  isItemSelectable?: (item: TreeItem) => boolean;
  level: number;
  columnWidths?: SelectTreeProps['columnWidths'];
  className?: string;
  selectedClassName?: string;
  onHoverItem?: (id: string, isHovering: boolean) => void;
}

interface SelectTreeLoadingNodeProps {
  disabled?: boolean;
  text: string;
}
export const SelectTreeLoadingNode = ({disabled, text}: SelectTreeLoadingNodeProps): JSX.Element => (
  <SelectTreeListItem
    disabled={disabled}
    ariaLabel={text}
    label={<span className="u-faded">{text}</span>}
    onClick={noop}
    onRequestToggleExpand={noop}
  />
);

interface SelectTreeLoadMoreNodeProps {
  disabled?: boolean;
  onLoadMore: (e: ElementMouseEvent) => void;
}
export const SelectTreeLoadMoreNode = ({disabled, onLoadMore}: SelectTreeLoadMoreNodeProps): JSX.Element => (
  <SelectTreeListItem
    disabled={disabled}
    label={<a>Load more</a>}
    ariaLabel="Load more"
    onClick={onLoadMore}
    onRequestToggleExpand={noop}
  />
);

interface SelectTreeNotFoundNodeProps {
  disabled?: boolean;
  text: string;
}
export const SelectTreeNotFoundNode = ({disabled, text}: SelectTreeNotFoundNodeProps): JSX.Element => (
  <SelectTreeListItem
    disabled={disabled}
    ariaLabel={text}
    label={<span className="u-faded">{text}</span>}
    onClick={noop}
    onRequestToggleExpand={noop}
  />
);

export default class SelectTreeNode extends React.Component<SelectTreeNodeProps> {
  static defaultProps = {
    isParentSelected: false,
  };

  private getDescendantItemIds(): Array<string> {
    const itemIdsToVisit = [this.props.item.id];
    const descendantItemIds = [];
    while (itemIdsToVisit.length) {
      const currentItemId = itemIdsToVisit.pop()!;
      descendantItemIds.push(currentItemId);
      if (this.props.itemStateMap[currentItemId]) {
        const leafItems = this.props.itemStateMap[currentItemId].leafItems;
        for (const leafItem of leafItems) {
          descendantItemIds.push(leafItem.id);
        }
      }
      // eslint-disable-next-line benchlint/no-in-operator
      if (currentItemId in this.props.itemStateMap) {
        this.props.itemStateMap[currentItemId].childItems.forEach((childItem) => {
          itemIdsToVisit.push(childItem.id);
        });
      }
    }
    return descendantItemIds;
  }

  private handleItemClick = (e: ElementMouseEvent): void => {
    // Prevent selecting an item if it is implied to be selected via its parent.
    if (this.props.multiple && this.props.isParentSelected) {
      return;
    }
    const id = this.props.item.id;
    const currentSelectedIds = this.props.selectedIds;
    let nextSelectedIds: Array<string>;
    if (!this.props.multiple) {
      // (single)
      nextSelectedIds = [id];
    } else if (currentSelectedIds.includes(id)) {
      // (multiple) selected, so deselect
      // eslint-disable-next-line no-restricted-syntax
      nextSelectedIds = _.without(currentSelectedIds, id);
    } else {
      // (multiple) not selected, so select
      nextSelectedIds = _.chain(currentSelectedIds)
        .without(...this.getDescendantItemIds())
        .union([id])
        .value();
    }
    if (e.shiftKey && this.props.multiple) {
      nextSelectedIds = this.props.getNextSelectedIds(id, nextSelectedIds);
    }
    this.props.onClickItem(id);
    this.props.onChangeSelectedIds(nextSelectedIds);
  };

  private handleToggleExpand = (): void => {
    if (this.isExpandable()) {
      this.props.toggleExpanded(this.props.item.id);
    }
  };

  private handleLoadMoreClick = (e: ElementMouseEvent): void => {
    this.props.loadMoreFromIds!([this.props.item.id]);
  };

  private canLoadMore(): boolean {
    return Boolean(this.props.loadMoreFromIds);
  }

  /**
   * A node can be expanded if it not a leaf (this.props.itemStateMap[id] is present), and it has children or
   * can load items.
   */
  private isExpandable(): boolean {
    const itemState = this.props.itemStateMap[this.props.item.id];
    if (itemState == null) {
      return false;
    }
    const canLoadItems =
      this.canLoadMore() &&
      (this.props.isLoadedItemExpandable != null ? this.props.isLoadedItemExpandable(this.props.item) : true);
    return (
      itemState.childItems.length > 0 ||
      itemState.leafItems.length > 0 ||
      itemState.baseLeafItems.length > 0 ||
      canLoadItems
    );
  }

  private isItemSelectable(item: TreeItem): boolean {
    return this.props.isItemSelectable ? this.props.isItemSelectable(item) : true;
  }

  render(): JSX.Element {
    const {id, icon, user, name} = this.props.item;
    const tooltipText =
      this.props.item.tooltipText ?? (this.props.requiredRootIds?.includes(id) ? 'Required' : null);
    const itemState = this.props.itemStateMap[id];

    // true: open, false: closed, null: not expandable
    const isExpanded: boolean | null = this.isExpandable() ? itemState.isExpanded : null;
    const isSelectedDirectly = this.props.selectedIds.includes(id);
    const isFolderSelected = (this.props.multiple && this.props.isParentSelected) || isSelectedDirectly;
    const nameAltText = user ? `${name} (${user})` : name;

    return (
      <SelectTreeListItem
        disabled={this.props.disabled || this.props.requiredRootIds?.includes(id)}
        id={id}
        onClick={this.isItemSelectable(this.props.item) ? this.handleItemClick : this.handleToggleExpand}
        onRequestToggleExpand={this.handleToggleExpand}
        isExpanded={isExpanded}
        ariaLabel={name}
        label={
          <span title={nameAltText}>
            {name}
            {user && <span className="u-tiny-spacing-left faded">{user}</span>}
          </span>
        }
        icon={icon}
        isSelected={isFolderSelected}
        isSelectedDirectly={isSelectedDirectly}
        tooltipText={tooltipText}
        labels={this.props.item.secondaryLabels}
        columnWidths={this.props.columnWidths}
        level={this.props.level}
        className={this.props.className}
        selectedClassName={this.props.selectedClassName}
        onHover={this.props.onHoverItem}
      >
        {(() => {
          if (!isExpanded) {
            return null;
          }
          const {childItems, baseLeafItems, leafItems, loading, more} = itemState;

          // Keep base leaf items at the top unless it is loaded into leafItems
          const firstLeafItems = baseLeafItems.filter((item) => !_.findWhere(leafItems, {id: item.id}));
          const allItems = [...childItems, ...firstLeafItems, ...leafItems];

          return (
            <ul>
              {allItems.map((childOrLeafItem) => (
                <SelectTreeNode
                  disabled={this.props.childrenDisabled || childOrLeafItem.disabled}
                  childrenDisabled={this.props.childrenDisabled}
                  key={childOrLeafItem.id}
                  item={childOrLeafItem}
                  onClickItem={this.props.onClickItem}
                  toggleExpanded={this.props.toggleExpanded}
                  itemStateMap={this.props.itemStateMap}
                  itemTypeName={this.props.itemTypeName}
                  isParentSelected={isFolderSelected}
                  selectedIds={this.props.selectedIds}
                  onChangeSelectedIds={this.props.onChangeSelectedIds}
                  multiple={this.props.multiple}
                  loadMoreFromIds={this.props.loadMoreFromIds}
                  getNextSelectedIds={this.props.getNextSelectedIds}
                  isLoadedItemExpandable={this.props.isLoadedItemExpandable}
                  isItemSelectable={this.props.isItemSelectable}
                  level={this.props.level + 1}
                  columnWidths={this.props.columnWidths}
                  className={this.props.className}
                  selectedClassName={this.props.selectedClassName}
                  onHoverItem={this.props.onHoverItem}
                />
              ))}
              {(() => {
                if (loading) {
                  return (
                    <SelectTreeLoadingNode
                      disabled={this.props.childrenDisabled}
                      text={`Loading ${this.props.itemTypeName}...`}
                    />
                  );
                } else if (more) {
                  return (
                    <SelectTreeLoadMoreNode
                      disabled={this.props.childrenDisabled}
                      onLoadMore={this.handleLoadMoreClick}
                    />
                  );
                } else if (this.canLoadMore() && allItems.length === 0) {
                  return (
                    <SelectTreeNotFoundNode
                      disabled={this.props.childrenDisabled}
                      text={`No ${this.props.itemTypeName} found.`}
                    />
                  );
                } else {
                  return null;
                }
              })()}
            </ul>
          );
        })()}
      </SelectTreeListItem>
    );
  }
}
